"use client";

import { useFormContext } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/ImageUpload";
import CurrencyInput from "@/components/CurrencyInput";
import MultiProductSelector from "@/components/MultiProductSelector";
import { useState, useEffect } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ProductsService } from "@/services/ProductsService";
import { FileService } from "@/services/FileService";
import { numberToMoney } from "@/util/moneyConversion";
import { AdditionalItemType } from "@/interfaces/products";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface OptionItemEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupIndex: number;
  itemIndex: number;
}

export default function OptionItemEditModal({
  isOpen,
  onClose,
  groupIndex,
  itemIndex,
}: OptionItemEditModalProps) {
  const { control, setValue, watch } = useFormContext();
  const itemPath = `additional.${groupIndex}.items.${itemIndex}`;
  const productId = watch(`${itemPath}.productId`);
  const fileId = watch(`${itemPath}.fileId`);
  const unitCost = watch(`${itemPath}.unitCost`) || 0;
  const unitPrice = watch(`${itemPath}.unitPrice`) || 0;
  const marginValue = unitPrice - unitCost;
  const [initialImage, setInitialImage] = useState<string | undefined>();
  const [itemType, setItemType] = useState<AdditionalItemType>(
    productId ? AdditionalItemType.PRODUCT : AdditionalItemType.CUSTOM
  );

  useEffect(() => {
    setValue(`${itemPath}.type`, itemType, { shouldValidate: true });
  }, [itemType, itemPath, setValue]);

  // Carregar dados do produto quando productId mudar
  useEffect(() => {
    const productsService = new ProductsService();

    const loadProduct = async () => {
      if (productId) {
        try {
          const product = await productsService.findById(productId, {
            expand: ["file"],
          });
          setValue(`${itemPath}.name`, product.name, { shouldValidate: true });
          setValue(`${itemPath}.unitCost`, product.unitCost || 0, {
            shouldValidate: true,
          });
          setValue(`${itemPath}.unitPrice`, product.unitPrice || 0, {
            shouldValidate: true,
          });
          setValue(`${itemPath}.fileId`, product.file?.id ?? undefined, {
            shouldValidate: true,
          });
          setInitialImage(product.file?.url);
        } catch (error) {
          console.error("Erro ao carregar produto:", error);
          // noop
        }
      } else {
        // noop
      }
    };

    loadProduct();
  }, [productId, itemPath, setValue]);

  useEffect(() => {
    const fetchFile = async () => {
      if (fileId) {
        try {
          const file = await new FileService().findById(fileId);
          setInitialImage(file.url);
        } catch (error) {
          console.error("Erro ao carregar imagem:", error);
        }
      } else {
        setInitialImage(undefined);
      }
    };

    fetchFile();
  }, [fileId]);

  const handleProductChange = async (selectedIds: string[]) => {
    const newProductId = selectedIds[0] || null; // Pega apenas o primeiro produto

    if (newProductId) {
      setValue(`${itemPath}.productId`, newProductId, { shouldValidate: true });
      try {
        const product = await new ProductsService().findById(newProductId, {
          expand: ["file"],
        });
        setValue(`${itemPath}.name`, product.name, { shouldValidate: true });
        setValue(`${itemPath}.unitCost`, product.unitCost || 0, {
          shouldValidate: true,
        });
        setValue(`${itemPath}.unitPrice`, product.unitPrice || 0, {
          shouldValidate: true,
        });
        setValue(`${itemPath}.fileId`, product.file?.id ?? undefined, {
          shouldValidate: true,
        });
        setValue(`${itemPath}.file`, undefined);
        setInitialImage(product.file?.url);
      } catch (error) {
        console.error("Erro ao carregar produto:", error);
      }
    } else {
      // Limpa a associação, tornando os campos editáveis
      setValue(`${itemPath}.productId`, null, { shouldValidate: true });
      setValue(`${itemPath}.name`, "", { shouldValidate: true });
      setValue(`${itemPath}.unitCost`, 0, { shouldValidate: true });
      setValue(`${itemPath}.unitPrice`, 0, { shouldValidate: true });
      setValue(`${itemPath}.fileId`, undefined);
      setValue(`${itemPath}.file`, undefined);
      setInitialImage(undefined);
    }
  };

  const handleItemTypeChange = (value: AdditionalItemType) => {
    setItemType(value);
    setValue(`${itemPath}.type`, value, { shouldValidate: true });
    if (value === AdditionalItemType.CUSTOM) {
      setValue(`${itemPath}.productId`, null, { shouldValidate: true });
      setValue(`${itemPath}.name`, "", { shouldValidate: true });
      setValue(`${itemPath}.unitCost`, 0, { shouldValidate: true });
      setValue(`${itemPath}.fileId`, undefined);
      setValue(`${itemPath}.file`, undefined);
      setValue(`${itemPath}.unitPrice`, 0, { shouldValidate: true });
      setInitialImage(undefined);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-3">
          <DialogTitle className="text-lg">
            Editar Item do Adicional
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Tipo de Item - Compacto */}
          <FormItem>
            <FormLabel className="text-sm font-medium">Tipo de Item</FormLabel>
            <RadioGroup
              value={itemType}
              onValueChange={(val) =>
                handleItemTypeChange(val as AdditionalItemType)
              }
              className="flex gap-6"
            >
              <FormItem className="flex items-center space-x-2 space-y-0">
                <FormControl>
                  <RadioGroupItem value={AdditionalItemType.PRODUCT} />
                </FormControl>
                <FormLabel className="text-sm font-normal">
                  Produto existente
                </FormLabel>
              </FormItem>
              <FormItem className="flex items-center space-x-2 space-y-0">
                <FormControl>
                  <RadioGroupItem value={AdditionalItemType.CUSTOM} />
                </FormControl>
                <FormLabel className="text-sm font-normal">
                  Personalizado
                </FormLabel>
              </FormItem>
            </RadioGroup>
          </FormItem>

          {/* Grid Principal - 2 colunas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Coluna A - Informações Básicas */}
            <div className="space-y-4">
              {itemType === AdditionalItemType.PRODUCT && (
                <FormField
                  control={control}
                  name={`${itemPath}.productId`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Produto Base
                      </FormLabel>
                      <FormControl>
                        <MultiProductSelector
                          value={field.value ? [field.value] : []}
                          onChange={handleProductChange}
                          placeholder="Selecione um produto"
                          selectionMode="single"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={control}
                name={`${itemPath}.name`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Nome do Item
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ex: Queijo Cheddar"
                        {...field}
                        className="h-10"
                        disabled={itemType === AdditionalItemType.PRODUCT}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Fator de Consumo - Largura completa no topo */}
              <FormField
                control={control}
                name={`${itemPath}.consumptionFactor`}
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-1">
                      <FormLabel className="text-sm font-medium">
                        Fator de Consumo
                      </FormLabel>
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center"
                            >
                              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-xs z-50"
                            sideOffset={5}
                          >
                            <p className="text-sm">
                              Define quantas unidades deste item são necessárias
                              para produzir uma unidade do produto final. Ex:
                              2.5 significa que são necessárias 2.5 unidades
                              deste item.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="1.0"
                        className="h-10 text-center max-w-32"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Preços - Grid 2 colunas (Custo e Preço lado a lado) */}
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={control}
                  name={`${itemPath}.unitCost`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Custo
                      </FormLabel>
                      <FormControl>
                        <CurrencyInput
                          placeholder="0,00"
                          {...field}
                          className="h-10"
                          disabled={itemType === AdditionalItemType.PRODUCT}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name={`${itemPath}.unitPrice`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Preço
                      </FormLabel>
                      <FormControl>
                        <CurrencyInput
                          placeholder="0,00"
                          {...field}
                          className="h-10"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Análise de Margem */}
              <div className="p-3 bg-muted/30 rounded-lg border">
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  Análise Financeira
                </div>
                {unitPrice > 0 && unitCost > 0 ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        Margem:
                      </span>
                      <span
                        className={`font-bold text-sm ${
                          marginValue >= 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {((marginValue / unitPrice) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        Lucro:
                      </span>
                      <span
                        className={`font-medium text-sm ${
                          marginValue >= 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {numberToMoney(marginValue)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-1">
                    Defina preço e custo para calcular margem
                  </div>
                )}
              </div>
            </div>

            {/* Coluna B - Imagem */}
            <div className="space-y-4">
              <FormField
                control={control}
                name={`${itemPath}.file`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Imagem
                    </FormLabel>
                    <FormControl>
                      <div className="w-full max-w-[200px] mx-auto">
                        <ImageUpload
                          initialImage={initialImage}
                          onImageChange={(file) => {
                            field.onChange(file || undefined);
                            setValue(`${itemPath}.fileId`, undefined);
                            if (!file) {
                              setInitialImage(undefined);
                            }
                          }}
                          disabled={itemType === AdditionalItemType.PRODUCT}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <DialogClose asChild>
            <Button
              type="button"
              className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white w-full sm:w-auto"
            >
              Salvar Alterações
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
